import React, { useState, useCallback, useMemo } from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { makeStyles } from "@material-ui/core/styles";
import prescriptionIcon from "../../images/prescription.svg";
import PrenscriptionDialog from "../prescription/PrenscriptionDialog";
import useUserState from "../hooks/useUserState";
import Axios from "axios";
import useBDocTranslator from "../hooks/useBDocTranslator";
import CircularProgress from "@material-ui/core/CircularProgress";
import swal from "sweetalert";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: "whitesmoke",
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: "900px",
    position: "relative",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    borderRadius: "4px",
  },
  content: {
    position: "relative",
    width: "100%",
    opacity: (props) => (props.isLoading ? 0.6 : 1),
    pointerEvents: (props) => (props.isLoading ? "none" : "auto"),
    transition: "opacity 0.2s ease-in-out",
  },
}));

export default function PrescriptionModalForm(props) {
  const [notes, setNotes] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dispenseTo, setDispenseTo] = useState("");
  const [pharmacy, setPharmacy] = useState("select");

  const userLocalState = useUserState();
  const { t } = useBDocTranslator();
  const classes = useStyles({ isLoading });

  const resetForm = useCallback(() => {
    setNotes("");
    setPharmacy("select");
    setDispenseTo("");
  }, []);

  const handleNotes = useCallback((value) => {
    setNotes(value);
  }, []);

  const prescriptionData = useMemo(
    () => ({
      patient_directions: notes,
      created_by: userLocalState[0].user.id,
      appointment_id: props.appointmentId,
      patient_id: props.patientId,
      dispense_from: new Date(),
      dispense_to: dispenseTo,
      pharmacy_id: pharmacy !== null && pharmacy !== "select" ? pharmacy : null,
    }),
    [
      notes,
      userLocalState,
      props.appointmentId,
      props.patientId,
      dispenseTo,
      pharmacy,
    ]
  );

  const handlePrescriptionSave = useCallback(async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL_BASE}/prenscriptions/create/only`,
        prescriptionData,
        {
          headers: { Authorization: "Bearer " + userLocalState[0].jwt },
        }
      );

      if (response?.data?.message) {
        setResponseMessage(response.data.message);
        resetForm();

        // Notify success and close modal
        await swal({
          title: t("Success"),
          text: t("Prescription created successfully"),
          icon: "success",
          buttons: false,
          timer: 2000,
        });

        props.handleClose();
        if (props.handleRefreshData) {
          props.handleRefreshData();
        }
      }
    } catch (error) {
      console.error("Prescription creation error:", error);
      swal({
        title: t("Error"),
        text: t("Error creating prescription"),
        icon: "error",
      });
      setResponseMessage(
        error?.response?.data?.message || t("Error creating prescription")
      );
    } finally {
      setIsLoading(false);
    }
  }, [prescriptionData, userLocalState, isLoading, props, t, resetForm]);

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={props.open}
        onClose={!isLoading ? props.handleClose : undefined}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={props.open}>
          <div className={classes.paper}>
            {isLoading && (
              <div className={classes.loadingOverlay}>
                <CircularProgress />
              </div>
            )}
            <div className={classes.content}>
              <div className="row col-md-12">
                <div className="col-md-4">
                  <img
                    src={prescriptionIcon}
                    alt="rx icon"
                    className="w-30-custom"
                  />
                </div>
                <div className="col-md-8">
                  <h3>{t("Create Prescription")}</h3>
                </div>
              </div>
              <div className="row col-md-12">
                <PrenscriptionDialog
                  handleNotes={handleNotes}
                  prescriptionValue={notes}
                  setDispenseTo={setDispenseTo}
                  dispenseTo={dispenseTo}
                  setPharmacy={setPharmacy}
                  pharmacy={pharmacy}
                  handleSave={handlePrescriptionSave}
                  responseMessage={responseMessage}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
    </div>
  );
}
